import { inject, Injectable } from '@angular/core';
import { LayerType } from '@cloud-twin-v2/generated/simulation.generated.client';
import { ApiRequestBuilderSettings } from '@common-modules/shared/api-request-builder/api-request-builder-settings';
import { ApiRequestBuilderService } from '@common-modules/shared/api-request-builder/api-request-builder.service';
import { Leaflet } from '@common-modules/wlm-map/leaflet-export';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import proj4 from 'proj4';
import { map, Observable } from 'rxjs';
import { GeoserverLayerProviderSettings } from '../models/geoserver-map-provider-settings';
import { LayerItemV2 } from '../models/layer-item';
import { MapCssStyle } from '../models/layer-type';
import { MapCoordinates } from '../models/map-coordinates';
import { MapProvider } from '../models/map-provider-settings';
import { NativeLayer } from '../models/native-layer';
import { BaseLayerProviderService } from './base-layer-provider.service';
import { GenericMapStylesService } from './generic-map-styles.service';

@Injectable()
export class GeoserverLayerProviderService extends BaseLayerProviderService {
  private readonly _genericMapStylesService = inject(GenericMapStylesService);
  private readonly _apiRequestBuilderService = inject(ApiRequestBuilderService);

  type = MapProvider.Geoserver;

  private readonly CRS = Leaflet.CRS.EPSG3857;

  getWMS(): Observable<NativeLayer> {
    throw new Error('Method not implemented.');
  }

  getWFS(layerItem: LayerItemV2): Observable<NativeLayer> {
    const layerType = layerItem.layerDefinition.layerType;
    return this.getWFSLayer(layerItem, layerType === LayerType.Point);
  }

  private getWFSLayer(layerItem: LayerItemV2, isPoint: boolean = false): Observable<NativeLayer> {
    const wfsSettings = layerItem.layerProvider as GeoserverLayerProviderSettings;
    const styles = layerItem.layerStyle?.styleDefinition as MapCssStyle;

    return this.getData(wfsSettings, layerItem).pipe(
      map((geojson: any) => {
        let mappedGeoJson = this.transformGeoJsonCoordinatesTo4326(geojson);
        if (isPoint) {
          return this.createPointGeoJsonLayer(mappedGeoJson, styles, layerItem);
        } else {
          return this.createNonPointGeoJsonLayer(mappedGeoJson, styles, layerItem);
        }
      }),
    );
  }

  private getData(
    settings: GeoserverLayerProviderSettings,
    layerItem: LayerItemV2,
  ): Observable<any> {
    const payload = this.buildWfsPayload(settings, layerItem);
    const requestSettings = new ApiRequestBuilderSettings({
      endpoint: settings.url,
      method: 'post',
      body: payload,
      absoluteUrl: true,
    });

    return this._apiRequestBuilderService.buildRequest(requestSettings);
  }

  private createPointGeoJsonLayer(geojson: any, styles: MapCssStyle, layerItem: LayerItemV2) {
    return Leaflet.geoJSON(geojson, {
      pointToLayer: (feature, latLng: MapCoordinates) => {
        const iconPath = this.getCurrentIcon(styles, layerItem);
        const isNodeType = layerItem.layerStyle.objectType === 'Node';
        const isThematic = layerItem.layerDefinition?.isThematic;

        let marker;

        if (!iconPath || isNodeType || isThematic) {
          const circleOptions = {
            radius: 3,
            fillOpacity: 1,
            size: 3,
            pane: layerItem.pane,
            ...(layerItem.layerName === 'Node'
              ? { color: styles.definition.Color }
              : styles?.definition),
          };

          marker = Leaflet.circleMarker(latLng, circleOptions);
          feature.properties.isCircleMarker = true;
        } else {
          marker = Leaflet.marker(latLng, {
            icon: this._genericMapStylesService.buildIcon(iconPath, styles),
            pane: layerItem.pane,
          });
          feature.properties.isIcon = true;
        }

        feature._markerRef = marker;
        return marker;
      },
      style: styles?.definition,
      pane: layerItem.pane,
    });
  }

  private createNonPointGeoJsonLayer(geojson: any, styles: MapCssStyle, layerItem: LayerItemV2) {
    return Leaflet.geoJSON(geojson, {
      crs: this.CRS,
      style: styles?.definition,
      pane: layerItem.pane,
    });
  }

  /**
   * Generates the WFS payload for a POST request based on configuration and filters.
   */
  private buildWfsPayload(
    settings: GeoserverLayerProviderSettings,
    layerItem: LayerItemV2,
  ): string {
    const typeName = `${settings.typeNS}:${settings.typename}`;
    const srsName = 'EPSG:3857';

    const filtersXml = this.buildOGCFilters(layerItem.filters).toGml();

    const payload = `
    <wfs:GetFeature xmlns:wfs="http://www.opengis.net/wfs" service="WFS" version="1.1.0" outputFormat="application/json">
      <wfs:Query typeName="${typeName}" srsName="${srsName}">
        <ogc:Filter xmlns:ogc="http://www.opengis.net/ogc">
          ${filtersXml.outerHTML}
        </ogc:Filter>
      </wfs:Query>
    </wfs:GetFeature>
    `.trim();

    return payload;
  }

  private getCurrentIcon = (styles: MapCssStyle, layerItem: LayerItemV2) =>
    styles.definition?.iconPath ?? layerItem.iconPath;

  private buildOGCFilters(descriptor: CompositeFilterDescriptor | FilterDescriptor): any {
    if (!descriptor) {
      return null;
    }

    const groupDescriptor = descriptor as CompositeFilterDescriptor;
    if (groupDescriptor.filters) {
      const filters = groupDescriptor.filters.map((filter) =>
        this.buildOGCFilters(filter as CompositeFilterDescriptor),
      );

      return groupDescriptor.logic === 'or'
        ? new Leaflet.Filter.Or(...filters)
        : new Leaflet.Filter.And(...filters);
    }

    const filterDescriptor = descriptor as FilterDescriptor;
    return new Leaflet.Filter.EQ(filterDescriptor.field, filterDescriptor.value);
  }

  /**
   * Transforms all coordinates of a geojson from EPSG:3857 to EPSG:4326 using proj4.
   * Returns the geojson with mapped coordinates.
   */
  transformGeoJsonCoordinatesTo4326(geojson: any): any {
    const epsg3857 = 'EPSG:3857';
    const epsg4326 = 'EPSG:4326';

    function transformCoords(coords: any): any {
      if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        // [x, y] -> [lng, lat]
        const [x, y] = coords;
        const [lng, lat] = proj4(epsg3857, epsg4326, [x, y]);
        return [lng, lat];
      } else if (Array.isArray(coords)) {
        return coords.map(transformCoords);
      }
      return coords;
    }

    function transformGeometry(geometry: any): any {
      if (!geometry) return geometry;
      const { type, coordinates, geometries } = geometry;
      if (coordinates) {
        return {
          ...geometry,
          coordinates: transformCoords(coordinates),
        };
      } else if (geometries) {
        return {
          ...geometry,
          geometries: geometries.map(transformGeometry),
        };
      }
      return geometry;
    }

    function transformFeature(feature: any): any {
      if (feature.geometry) {
        return {
          ...feature,
          geometry: transformGeometry(feature.geometry),
        };
      }
      return feature;
    }

    if (geojson.type === 'FeatureCollection') {
      return {
        ...geojson,
        features: geojson.features.map(transformFeature),
      };
    } else if (geojson.type === 'Feature') {
      return transformFeature(geojson);
    } else if (geojson.type && geojson.coordinates) {
      return transformGeometry(geojson);
    }
    return geojson;
  }

  /**
   * Filters a geojson and returns a new geojson with only the features that have
   * the property 'propertyName' equal to 'value'.
   */
  filterGeoJsonByPropertyValue(geojson: any, propertyName: string, value: any): any {
    if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
      return geojson;
    }
    const filteredFeatures = geojson.features.filter(
      (feature: any) => feature?.properties?.[propertyName] === value,
    );
    return {
      ...geojson,
      features: filteredFeatures,
    };
  }
}
